import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Flame, Heart, Plus, Trash2, Users, ChevronDown, ChevronUp, Play, Sparkles, X
} from 'lucide-react';
import { toast } from 'sonner';
import ExerciseSearchSelect from './ExerciseSearchSelect';

const WOD_CATEGORIES = [
  { id: 'force', label: 'Force', color: '#f59e0b' },
  { id: 'cardio', label: 'Cardio', color: '#ef4444' },
  { id: 'hiit', label: 'HIIT', color: '#8b5cf6' },
  { id: 'wod', label: 'WOD', color: '#ec4899' },
  { id: 'custom', label: 'Custom', color: '#06b6d4' },
];

export default function WodSection({ catalog, onUseWod }) {
  const [wods, setWods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState(null);

  // Create form
  const [wodName, setWodName] = useState('');
  const [wodDesc, setWodDesc] = useState('');
  const [wodCategory, setWodCategory] = useState('wod');
  const [wodExercises, setWodExercises] = useState([]);
  const [saving, setSaving] = useState(false);

  // Temp state for adding exercise to WOD
  const [addingExercise, setAddingExercise] = useState(false);
  const [tempCat, setTempCat] = useState('machine');
  const [tempEx, setTempEx] = useState('');
  const [tempSets, setTempSets] = useState('3');
  const [tempReps, setTempReps] = useState('10');
  const [tempRest, setTempRest] = useState('60');

  const fetchWods = useCallback(async () => {
    try {
      const res = await apiClient.get('/wods');
      setWods(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWods(); }, [fetchWods]);

  const handleCreateWod = async (e) => {
    e.preventDefault();
    if (!wodName.trim() || wodExercises.length === 0) {
      toast.error('Nom et au moins 1 exercice requis');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/wods', {
        name: wodName,
        description: wodDesc,
        exercises: wodExercises,
        category: wodCategory,
        shared: true,
      });
      toast.success('WOD cree et partage !');
      setShowCreate(false);
      setWodName(''); setWodDesc(''); setWodExercises([]);
      fetchWods();
    } catch {
      toast.error('Erreur creation WOD');
    } finally {
      setSaving(false);
    }
  };

  const addExerciseToWod = () => {
    if (!tempEx) return;
    // Find exercise name from catalog
    let exName = tempEx;
    if (catalog) {
      for (const catData of Object.values(catalog)) {
        const found = catData.exercises?.find(e => e.id === tempEx);
        if (found) { exName = found.name; break; }
      }
    }
    setWodExercises(prev => [...prev, {
      exercise_id: tempEx,
      exercise_name: exName,
      sets: parseInt(tempSets) || 3,
      reps: tempReps || '10',
      rest_seconds: parseInt(tempRest) || 60,
    }]);
    setAddingExercise(false);
    setTempEx('');
  };

  const removeExFromWod = (index) => {
    setWodExercises(prev => prev.filter((_, i) => i !== index));
  };

  const toggleLike = async (wodId) => {
    try {
      const res = await apiClient.post(`/wods/${wodId}/like`);
      setWods(prev => prev.map(w => w.id === wodId ? { ...w, liked_by_me: res.data.action === 'liked', like_count: res.data.like_count } : w));
    } catch {
      toast.error('Erreur');
    }
  };

  const handleApplyWod = async (wod) => {
    try {
      const res = await apiClient.post(`/wods/${wod.id}/use`);
      toast.success(`WOD charge !`);
      onUseWod?.(res.data);
    } catch {
      toast.error('Erreur');
    }
  };

  const deleteWod = async (wodId) => {
    try {
      await apiClient.delete(`/wods/${wodId}`);
      toast.success('WOD supprime');
      fetchWods();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-4" data-testid="wod-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
          <Flame className="w-4 h-4 text-[#FF6B35]" /> WODs & Sets partages
        </h3>
        <Button
          onClick={() => setShowCreate(!showCreate)}
          size="sm"
          className="bg-[#FF6B35] text-black hover:bg-[#E85A25] text-xs"
          data-testid="create-wod-button"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Creer un WOD
        </Button>
      </div>

      {/* Create WOD form */}
      {showCreate && (
        <form onSubmit={handleCreateWod} className="bg-[#1B1B1B] border border-[#FF6B35]/30 rounded-sm p-4 space-y-3" data-testid="create-wod-form">
          <h4 className="text-sm font-medium text-[#FF6B35]">Nouveau WOD</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-zinc-500 text-[10px]">Nom du WOD *</Label>
              <Input value={wodName} onChange={e => setWodName(e.target.value)} placeholder="WOD Force Lundi" className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-sm" data-testid="wod-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-500 text-[10px]">Type</Label>
              <select value={wodCategory} onChange={e => setWodCategory(e.target.value)} className="w-full bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-sm px-3 py-2 text-sm" data-testid="wod-category-select">
                {WOD_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-500 text-[10px]">Description (optionnel)</Label>
            <Input value={wodDesc} onChange={e => setWodDesc(e.target.value)} placeholder="Circuit rapide de 20 min" className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-sm" data-testid="wod-desc-input" />
          </div>

          {/* Exercises in WOD */}
          <div className="space-y-2">
            <Label className="text-zinc-500 text-[10px]">Exercices ({wodExercises.length})</Label>
            {wodExercises.map((ex, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#0F0F0F] rounded-sm px-3 py-2 text-sm">
                <span className="text-white flex-1 truncate">{ex.exercise_name || ex.exercise_id}</span>
                <span className="text-zinc-500 text-xs">{ex.sets}x{ex.reps}</span>
                <span className="text-zinc-600 text-xs">{ex.rest_seconds}s</span>
                <button type="button" onClick={() => removeExFromWod(i)} className="text-zinc-500 hover:text-red-400">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {addingExercise ? (
              <div className="space-y-2 bg-[#0F0F0F] rounded-sm p-3 border border-[#2A2A2E]">
                <ExerciseSearchSelect
                  catalog={catalog}
                  selectedCategory={tempCat}
                  selectedExercise={tempEx}
                  onCategoryChange={setTempCat}
                  onExerciseChange={setTempEx}
                />
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-zinc-600 text-[10px]">Series</Label>
                    <Input type="number" value={tempSets} onChange={e => setTempSets(e.target.value)} className="bg-[#111] border-[#2A2A2E] text-white text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-600 text-[10px]">Reps</Label>
                    <Input value={tempReps} onChange={e => setTempReps(e.target.value)} className="bg-[#111] border-[#2A2A2E] text-white text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-600 text-[10px]">Repos (s)</Label>
                    <Input type="number" value={tempRest} onChange={e => setTempRest(e.target.value)} className="bg-[#111] border-[#2A2A2E] text-white text-xs" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setAddingExercise(false)} className="flex-1 border-zinc-700 text-zinc-400 text-xs">Annuler</Button>
                  <Button type="button" size="sm" onClick={addExerciseToWod} className="flex-1 bg-[#FF6B35] text-black text-xs" data-testid="confirm-add-wod-exercise">Ajouter</Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAddingExercise(true)}
                className="w-full py-2 border border-dashed border-[#2A2A2E] rounded-sm text-xs text-zinc-500 hover:text-[#FF6B35] hover:border-[#FF6B35]/50 transition-colors"
                data-testid="add-exercise-to-wod"
              >
                <Plus className="w-3.5 h-3.5 inline mr-1" /> Ajouter un exercice
              </button>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="save-wod-button">
              {saving ? 'Creation...' : 'Partager le WOD'}
            </Button>
          </div>
        </form>
      )}

      {/* WOD List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : wods.length === 0 ? (
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-6 text-center">
          <Flame className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
          <p className="text-zinc-400 text-sm">Aucun WOD partage</p>
          <p className="text-zinc-500 text-xs mt-1">Cree le premier pour le groupe !</p>
        </div>
      ) : (
        <div className="space-y-2">
          {wods.map(wod => {
            const catInfo = WOD_CATEGORIES.find(c => c.id === wod.category) || WOD_CATEGORIES[4];
            const isExpanded = expanded === wod.id;
            return (
              <div key={wod.id} className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden" data-testid={`wod-card-${wod.id}`}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded(isExpanded ? null : wod.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setExpanded(isExpanded ? null : wod.id); }}
                  className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#1a1a24] transition-colors cursor-pointer"
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: catInfo.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{wod.name}</p>
                    <p className="text-[10px] text-zinc-500">
                      {wod.creator_name} · {wod.exercises?.length || 0} exos · {wod.uses_count || 0} utilisations
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleLike(wod.id); }}
                      className={`p-1 ${wod.liked_by_me ? 'text-red-500' : 'text-zinc-500'}`}
                      data-testid={`like-wod-${wod.id}`}
                    >
                      <Heart className={`w-4 h-4 ${wod.liked_by_me ? 'fill-current' : ''}`} />
                    </button>
                    <span className="text-xs text-zinc-500">{wod.like_count || 0}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-[#2A2A2E] p-3 space-y-2">
                    {wod.description && <p className="text-xs text-zinc-400">{wod.description}</p>}
                    {wod.exercises?.map((ex, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs py-1">
                        <span className="text-zinc-500 w-5 text-right">{i + 1}.</span>
                        <span className="text-white flex-1 truncate">{ex.exercise_name || ex.exercise_id}</span>
                        <span className="text-zinc-500">{ex.sets}x{ex.reps}</span>
                        <span className="text-zinc-600">{ex.rest_seconds}s repos</span>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => handleApplyWod(wod)}
                        className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25] text-xs"
                        data-testid={`use-wod-${wod.id}`}
                      >
                        <Play className="w-3.5 h-3.5 mr-1" /> Utiliser ce WOD
                      </Button>
                      {wod.is_mine && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteWod(wod.id)}
                          className="border-zinc-700 text-zinc-400 hover:text-red-400 text-xs"
                          data-testid={`delete-wod-${wod.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
